import {
  type QueryClient,
  queryOptions,
  type Updater,
  useMutation,
  usePrefetchQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"

import type {
  RepositoryLocale,
  RepositoryLocaleUpsert,
} from "../../app/services/locales"
import * as locales from "../../app/services/locales"
import {
  addRepositoryLocale,
  deleteRepositoryLocale,
  setRepositoryLocaleAsMaster,
} from "./localeUtils"

export function useRepositoryLocalesQuery() {
  return useSuspenseQuery(repositoryLocalesQueryOptions)
}

interface UseAddRepositoryLocaleMutationOptions {
  onSuccess?: (repositoryLocale: RepositoryLocale) => void
  onError?: (repositoryLocaleUpsert: RepositoryLocaleUpsert) => void
}

export function useAddRepositoryLocaleMutation(
  options: UseAddRepositoryLocaleMutationOptions = {},
) {
  const { onSuccess, onError } = options

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: locales.upsertRepositoryLocale,
    onSuccess: (insertedRepositoryLocale) => {
      setRepositoryLocalesQueryData(queryClient, (oldRepositoryLocales) =>
        addRepositoryLocale(oldRepositoryLocales, insertedRepositoryLocale),
      )
      onSuccess?.(insertedRepositoryLocale)
    },
    onError: (_error, repositoryLocaleUpsert) => {
      onError?.(repositoryLocaleUpsert)
    },
  })
}

interface UseDeleteRepositoryLocaleMutationOptions {
  onSuccess?: (repositoryLocale: RepositoryLocale) => void
  onError?: (repositoryLocale: RepositoryLocale) => void
}

export function useDeleteRepositoryLocaleMutation(
  options: UseDeleteRepositoryLocaleMutationOptions = {},
) {
  const { onSuccess, onError } = options

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (repositoryLocale: RepositoryLocale) =>
      locales.deleteRepositoryLocale(repositoryLocale.id),
    onSuccess: (_response, repositoryLocale) => {
      setRepositoryLocalesQueryData(queryClient, (oldRepositoryLocales) =>
        deleteRepositoryLocale(oldRepositoryLocales, repositoryLocale.id),
      )
      onSuccess?.(repositoryLocale)
    },
    onError: (_error, repositoryLocale) => {
      onError?.(repositoryLocale)
    },
  })
}

interface UseSetRepositoryLocaleAsMasterMutationOptions {
  onSuccess?: (repositoryLocale: RepositoryLocale) => void
  onError?: (repositoryLocale: RepositoryLocale) => void
}

export type SetRepositoryLocaleAsMasterMutation = ReturnType<
  typeof useSetRepositoryLocaleAsMasterMutation
>

export function useSetRepositoryLocaleAsMasterMutation(
  options: UseSetRepositoryLocaleAsMasterMutationOptions = {},
) {
  const { onSuccess, onError } = options

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (repositoryLocale: RepositoryLocale) =>
      locales.upsertRepositoryLocale({ ...repositoryLocale, isMaster: true }),
    onSuccess: (updatedRepositoryLocale) => {
      setRepositoryLocalesQueryData(queryClient, (oldRepositoryLocales) =>
        setRepositoryLocaleAsMaster(
          oldRepositoryLocales,
          updatedRepositoryLocale.id,
        ),
      )
      onSuccess?.(updatedRepositoryLocale)
    },
    onError: (_error, repositoryLocale) => {
      onError?.(repositoryLocale)
    },
  })
}

const repositoryLocalesQueryOptions = queryOptions({
  queryKey: ["repositoryLocales"],
  queryFn: locales.getRepositoryLocales,
})

function setRepositoryLocalesQueryData(
  queryClient: QueryClient,
  updater: Updater<RepositoryLocale[], RepositoryLocale[]>,
): RepositoryLocale[] | undefined {
  return queryClient.setQueryData(
    repositoryLocalesQueryOptions.queryKey,
    (oldRepositoryLocales) => {
      if (typeof updater !== "function") return updater
      if (oldRepositoryLocales === undefined) return oldRepositoryLocales
      return updater(oldRepositoryLocales)
    },
  )
}

export function usePrefetchRepositoryLocalesQuery(): void {
  usePrefetchQuery(repositoryLocalesQueryOptions)
}
